import { z } from "zod";
import { actorIdentitySchema } from "./authority.js";
import { protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, verificationStatusSchema, } from "./identifiers.js";
export const universalCanaryScenarioSchema = z
    .object({
    id: protocolIdSchema,
    expectedVerdict: verificationStatusSchema,
    observedVerdict: verificationStatusSchema.nullable(),
    controlStatus: z.enum(["PASSED", "FAILED"]),
    reasonCodes: z.array(protocolIdSchema),
    receiptId: protocolIdSchema.nullable(),
    receiptPayloadDigest: sha256DigestSchema.nullable(),
})
    .strict();
export const legacyUniversalCanaryReceiptPointerSchema = z
    .object({
    id: protocolIdSchema,
    payloadDigest: sha256DigestSchema,
    signingKeyId: protocolIdSchema,
    issuedAt: protocolTimestampSchema,
})
    .strict()
    .nullable();
export const currentUniversalCanaryReceiptPointerSchema = z
    .object({
    id: protocolIdSchema,
    payloadDigest: sha256DigestSchema,
    completionSummaryDigest: sha256DigestSchema,
    signingKeyId: protocolIdSchema,
    issuedAt: protocolTimestampSchema,
})
    .strict()
    .nullable();
export const universalCanaryReceiptPointerSchema = z.union([
    currentUniversalCanaryReceiptPointerSchema,
    legacyUniversalCanaryReceiptPointerSchema,
]);
const externalFixtureReceiptSchema = z
    .object({
    id: protocolIdSchema,
    payloadDigest: sha256DigestSchema,
    issuedAt: protocolTimestampSchema,
})
    .strict()
    .nullable();
const universalCanaryRunCommonShape = {
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    trigger: z.enum(["scheduled", "protocol_change", "manual", "release"]),
    status: z.enum(["PASS", "FAIL"]),
    startedAt: protocolTimestampSchema,
    completedAt: protocolTimestampSchema,
    runner: actorIdentitySchema,
    adapterModes: z.array(z.enum(["fully_mediated", "cooperative", "passive"])).min(1),
    providerRefs: z.array(protocolIdSchema).min(1),
    environmentDigest: sha256DigestSchema,
    suiteDigest: sha256DigestSchema,
    scenarios: z.array(universalCanaryScenarioSchema).min(1),
    externalFixtureReceipt: externalFixtureReceiptSchema,
};
function addUniversalCanaryRunIssues(value, context) {
    const controlsPassed = value.scenarios.every((scenario) => scenario.controlStatus === "PASSED");
    if ((value.status === "PASS") !== controlsPassed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["status"],
            message: "canary status must equal the aggregate control status",
        });
    }
    if (value.status === "PASS" && !value.successfulReceipt) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["successfulReceipt"],
            message: "a passing canary requires a signed successful receipt",
        });
    }
    if (value.status === "PASS" && !value.externalFixtureReceipt) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["externalFixtureReceipt"],
            message: "a passing canary requires an offline-verified external fixture receipt",
        });
    }
}
export const currentUniversalCanaryRunSchema = z
    .object({
    ...universalCanaryRunCommonShape,
    completionProtocol: z.literal("task-outcome.v1"),
    successfulReceipt: currentUniversalCanaryReceiptPointerSchema,
})
    .strict()
    .superRefine(addUniversalCanaryRunIssues);
export const legacyUniversalCanaryRunSchema = z
    .object({
    ...universalCanaryRunCommonShape,
    successfulReceipt: legacyUniversalCanaryReceiptPointerSchema,
})
    .strict()
    .superRefine(addUniversalCanaryRunIssues);
// A short-lived pre-CU-W01 producer wrote the protocol discriminator before it
// wrote the completion-summary binding. Preserve that exact durable shape as
// historical input, but never classify it as a current run.
export const legacyCompletionTaggedUniversalCanaryRunSchema = z
    .object({
    ...universalCanaryRunCommonShape,
    completionProtocol: z.literal("task-outcome.v1"),
    successfulReceipt: legacyUniversalCanaryReceiptPointerSchema,
})
    .strict()
    .superRefine(addUniversalCanaryRunIssues);
export const universalCanaryRunSchema = z.union([
    currentUniversalCanaryRunSchema,
    legacyCompletionTaggedUniversalCanaryRunSchema,
    legacyUniversalCanaryRunSchema,
]);
export const universalDogfoodStatusSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    tenantId: protocolIdSchema,
    generatedAt: protocolTimestampSchema,
    freshness: z.enum(["FRESH", "STALE", "MISSING", "FAILING"]),
    maxAgeSeconds: z.number().int().positive(),
    latestRun: universalCanaryRunSchema.nullable(),
    lastSuccessfulReceipt: currentUniversalCanaryReceiptPointerSchema,
    externalPortabilityProven: z.boolean(),
    reasonCodes: z.array(protocolIdSchema),
})
    .strict();
export function deriveUniversalDogfoodStatus(input) {
    const maxAgeSeconds = input.maxAgeSeconds ?? 24 * 60 * 60;
    const allRuns = input.runs
        .map((run) => universalCanaryRunSchema.parse(run))
        .sort((left, right) => right.completedAt.localeCompare(left.completedAt));
    const latestRun = allRuns[0] ?? null;
    const latestCurrent = latestRun ? currentUniversalCanaryRunSchema.safeParse(latestRun) : null;
    const latestCurrentRun = latestCurrent?.success ? latestCurrent.data : null;
    const currentRuns = allRuns.flatMap((run) => {
        const parsed = currentUniversalCanaryRunSchema.safeParse(run);
        return parsed.success ? [parsed.data] : [];
    });
    const lastSuccessful = currentRuns.find((run) => run.status === "PASS" && run.successfulReceipt) ?? null;
    const ageSeconds = latestRun
        ? Math.max(0, (Date.parse(input.now) - Date.parse(latestRun.completedAt)) / 1000)
        : Number.POSITIVE_INFINITY;
    const freshness = !latestRun
        ? "MISSING"
        : !latestCurrentRun
            ? "STALE"
            : latestCurrentRun.status === "FAIL"
                ? "FAILING"
                : ageSeconds > maxAgeSeconds
                    ? "STALE"
                    : "FRESH";
    const reasonCodes = freshness === "FRESH"
        ? []
        : freshness === "MISSING"
            ? ["dogfood-run-missing"]
            : freshness === "FAILING"
                ? ["latest-dogfood-run-failed"]
                : !latestCurrentRun
                    ? ["dogfood-completion-metadata-missing"]
                    : ["dogfood-run-stale"];
    return universalDogfoodStatusSchema.parse({
        protocolVersion: "excellent.universal-verification.v1",
        tenantId: input.tenantId,
        generatedAt: input.now,
        freshness,
        maxAgeSeconds,
        latestRun,
        lastSuccessfulReceipt: lastSuccessful?.successfulReceipt ?? null,
        externalPortabilityProven: latestCurrentRun !== null &&
            latestCurrentRun.externalFixtureReceipt !== null &&
            latestCurrentRun.status === "PASS",
        reasonCodes,
    });
}
